import { createFileRoute, useParams, Link } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { AnalyticsShell, StatCard } from '../components/analytics/AnalyticsShell';
import { Stagger, AnimatedBar } from '../components/analytics/anim';
import { Donut, Legend, HBar, Funnel, AttentionBars, smoothRetention } from '../components/analytics/charts';

export const Route = createFileRoute('/analytics/$shareToken')({
  component: VideoAnalytics,
});

interface Agg {
  admin: boolean;
  conversationId: string;
  shareToken: string;
  title: string | null;
  kpis: {
    views: number;
    uniqueViewers: number;
    plays: number;
    completes: number;
    avgPercentViewed: number;
    avgWatchMs: number;
  };
  byDevice: Array<{ key: string; views: number }>;
  byCountry: Array<{ key: string; views: number }>;
  slideEngagement: Array<{ slideId: string; slideIndex: number; avgDwellSec: number; avgAttention: number; engagements: number }>;
  retention: Array<{ second: number; reached: number; plays: number }>;
  linkedAssessments?: Array<{
    shareToken: string;
    assessmentId: string | null;
    title: string | null;
    questionCount: number;
    attempts: number;
    answers: number;
    lastViewedAt: string | null;
  }>;
}

interface ViewRow {
  viewId: string;
  startedAt: string;
  endedAt: string | null;
  maxPercent: number;
  completed: boolean;
  deviceType: string | null;
  geoCountry: string | null;
  embedOrigin: string | null;
  referrer: string | null;
  anonShort: string;
  identifiedId: string | null;
  registration: string | null;
  hasCoverage: boolean;
  events: number;
}

interface TimelineEvent {
  seq: number;
  event: string;
  viewerTime: string;
  playheadMs: number | null;
  slideId: string | null;
  slideIndex: number | null;
  percent: number | null;
  properties: Record<string, unknown>;
}

function pct(n: number, d: number) {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}
function clock(ms: number | null) {
  if (ms == null) return '—';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
function dur(ms: number) {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
// Nullable, compact: sub-minute shows one decimal of seconds (1.2s); else m s.
function tms(ms: number | null | undefined) {
  if (ms == null) return '—';
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const PALETTE = ['hsl(var(--primary))', 'hsl(258 70% 72%)', 'hsl(172 66% 55%)', 'hsl(38 92% 60%)'];

function VideoAnalytics() {
  const { shareToken } = useParams({ from: '/analytics/$shareToken' });
  const [agg, setAgg] = useState<Agg | null>(null);
  const [views, setViews] = useState<ViewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'engagement' | 'assessments'>('engagement');
  const [viewer, setViewer] = useState<{ actor: string | null } | null>(null);

  useEffect(() => {
    api.get(`/analytics/dashboard/videos/${shareToken}`).then((r) => setAgg(r.data)).catch((e) =>
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load'),
    );
    api.get(`/analytics/dashboard/videos/${shareToken}/views`).then((r) => setViews(r.data.views ?? []));
  }, [shareToken]);

  const shownViews = viewer && views ? views.filter((v) => v.identifiedId === viewer.actor) : views;

  return (
    <AnalyticsShell crumb={agg?.title || 'Video'}>
        {error && <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</div>}
        {!agg && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

        {agg && (
          <>
            <div className="mb-6">
              <h1 className="text-[22px] font-semibold tracking-tight">{agg.title || '(untitled video)'}</h1>
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="font-mono">{agg.shareToken}</span>
                {' · '}
                <Link to="/analytics" search={{ video: agg.conversationId }} className="text-primary hover:underline">
                  All links for this video
                </Link>
              </p>
            </div>

            <div className="mb-6 flex gap-1 border-b border-border/60">
              {(['engagement', 'assessments'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium capitalize transition ${tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                  {t}
                </button>
              ))}
            </div>

            {tab === 'engagement' && (
              <>
            <Stagger className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard label="Views" value={agg.kpis.views} accent />
              <StatCard label="Unique viewers" value={agg.kpis.uniqueViewers} />
              <StatCard label="Avg watch time" value={dur(agg.kpis.avgWatchMs ?? 0)} hint={`${agg.kpis.avgPercentViewed ?? 0}% avg viewed · ${agg.kpis.plays} plays`} />
              <StatCard label="Completion" value={`${pct(agg.kpis.completes, agg.kpis.views)}%`} accent bar={pct(agg.kpis.completes, agg.kpis.views)} />
            </Stagger>

            <div className="space-y-5">
                <RetentionCurve data={agg.retention} totalViews={agg.kpis.views} />
                <div className="grid gap-5 lg:grid-cols-3">
                  <Panel title="Device">
                    {agg.byDevice.length === 0 ? (
                      <Empty>No device data yet.</Empty>
                    ) : (
                      <div className="flex items-center gap-5">
                        <Donut
                          segments={agg.byDevice.slice(0, 4).map((d, i) => ({ label: d.key, value: d.views, color: PALETTE[i % PALETTE.length] }))}
                          center={{ value: String(agg.kpis.views), label: 'views' }}
                        />
                        <Legend
                          items={agg.byDevice.slice(0, 4).map((d, i) => ({ label: d.key, value: `${pct(d.views, agg.kpis.views)}%`, color: PALETTE[i % PALETTE.length] }))}
                        />
                      </div>
                    )}
                  </Panel>
                  <Panel title="Top countries">
                    {agg.byCountry.length === 0 ? (
                      <Empty>No country data yet.</Empty>
                    ) : (
                      <div className="space-y-3">
                        {agg.byCountry.slice(0, 6).map((c) => (
                          <HBar key={c.key} label={c.key} pct={pct(c.views, agg.byCountry[0].views)} value={c.views} labelWidth="w-16" />
                        ))}
                      </div>
                    )}
                  </Panel>
                  <Panel title="Engagement funnel" subtitle="How far viewers progress">
                    <Funnel
                      steps={[
                        { label: 'Loaded', value: agg.kpis.views, pct: 100, dim: 0 },
                        { label: 'Played', value: agg.kpis.plays, pct: pct(agg.kpis.plays, agg.kpis.views), dim: 1 },
                        { label: 'Completed', value: agg.kpis.completes, pct: pct(agg.kpis.completes, agg.kpis.views), dim: 2 },
                      ]}
                    />
                  </Panel>
                </div>

              <Panel title="Attention by slide" subtitle="Average share of each slide actually watched — spot where viewers disengage (amber/red = drop-off)">
                  {agg.slideEngagement.length === 0 ? (
                    <Empty>No slide-engagement events yet.</Empty>
                  ) : (
                    <AttentionBars
                      rows={agg.slideEngagement.map((s) => ({
                        label: `${s.slideIndex + 1} · ${s.slideId}`,
                        pct: Math.round((s.avgAttention ?? 0) * 100),
                        meta: `${s.avgDwellSec}s`,
                      }))}
                    />
                  )}
                </Panel>
                {agg.linkedAssessments && agg.linkedAssessments.length > 0 && (
                  <LinkedAssessmentsPanel items={agg.linkedAssessments} />
                )}
                <ComprehensionPanel shareToken={shareToken} />
                <ViewersPanel shareToken={shareToken} selected={viewer} onSelect={setViewer} />

              <Panel
                title="Individual views"
                subtitle={
                  shownViews
                    ? `${shownViews.length} ${viewer ? `from ${viewer.actor ?? 'unknown viewers'}` : 'captured'} · click any row to inspect the full event timeline`
                    : undefined
                }
              >
                  {!shownViews ? (
                    <Empty>Loading views…</Empty>
                  ) : shownViews.length === 0 ? (
                    <Empty>No views yet.</Empty>
                  ) : (
                    <div className="-mx-5 -mb-5 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="border-b border-border/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="px-5 py-2.5 text-left font-medium">When</th>
                            <th className="px-5 py-2.5 text-left font-medium">Viewer</th>
                            <th className="px-5 py-2.5 text-left font-medium">Device</th>
                            <th className="px-5 py-2.5 text-left font-medium">Country</th>
                            <th className="px-5 py-2.5 text-right font-medium">% viewed</th>
                            <th className="px-5 py-2.5 text-center font-medium">Done</th>
                            <th className="px-5 py-2.5 text-right font-medium">Events</th>
                            <th className="px-5 py-2.5" />
                          </tr>
                        </thead>
                        <tbody>
                          {shownViews.map((v) => (
                            <tr
                              key={v.viewId}
                              onClick={() => setSelected(v.viewId)}
                              className="cursor-pointer border-b border-border/40 last:border-0 hover:bg-muted/40"
                            >
                              <td className="whitespace-nowrap px-5 py-2.5">{new Date(v.startedAt).toLocaleString()}</td>
                              <td className="max-w-[16rem] truncate px-5 py-2.5 font-mono text-xs text-muted-foreground">
                                {v.identifiedId && agg.admin ? (
                                  <Link
                                    to="/analytics/actors/$actorId"
                                    params={{ actorId: v.identifiedId }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-primary hover:underline"
                                  >
                                    {v.identifiedId}
                                  </Link>
                                ) : v.identifiedId ? (
                                  <span className="text-foreground">{v.identifiedId}</span>
                                ) : (
                                  `${v.anonShort}…`
                                )}
                              </td>
                              <td className="px-5 py-2.5">{v.deviceType ?? '—'}</td>
                              <td className="px-5 py-2.5">{v.geoCountry ?? '—'}</td>
                              <td className="px-5 py-2.5 text-right tabular-nums">{v.maxPercent}%</td>
                              <td className="px-5 py-2.5 text-center">{v.completed ? <span className="text-primary">✓</span> : ''}</td>
                              <td className="px-5 py-2.5 text-right tabular-nums">{v.events}</td>
                              <td className="px-5 py-2.5 text-right text-xs text-primary">Timeline →</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
            </div>
              </>
            )}

            {tab === 'assessments' && <AssessmentsPanel shareToken={shareToken} onSelect={setSelected} />}
          </>
        )}

      <AnimatePresence>
        {selected && <TimelineDrawer key={selected} viewId={selected} admin={Boolean(agg?.admin)} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </AnalyticsShell>
  );
}

interface ViewerRow {
  actor: string | null;
  views: number;
  devices: number;
  completes: number;
  avgPercentViewed: number;
  bestPercentViewed: number;
  watchMs: number;
  lastSeenAt: string;
}

function ViewersPanel({
  shareToken,
  selected,
  onSelect,
}: {
  shareToken: string;
  selected: { actor: string | null } | null;
  onSelect: (viewer: { actor: string | null } | null) => void;
}) {
  const [viewers, setViewers] = useState<ViewerRow[] | null>(null);

  useEffect(() => {
    api.get(`/analytics/dashboard/videos/${shareToken}/viewers`).then((r) => setViewers(r.data.viewers ?? []));
  }, [shareToken]);

  return (
    <Panel
      title="Viewers"
      subtitle="One row per person when the link was opened with ?actor=<id>; everyone else is counted together as unknown. Click a row to filter the views below."
    >
      {!viewers ? (
        <Empty>Loading viewers…</Empty>
      ) : viewers.length === 0 ? (
        <Empty>No views yet.</Empty>
      ) : (
        <div className="-mx-5 -mb-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-5 py-2.5 text-left font-medium">Viewer</th>
                <th className="px-5 py-2.5 text-right font-medium">Views</th>
                <th className="px-5 py-2.5 text-right font-medium">Completed</th>
                <th className="px-5 py-2.5 text-right font-medium">Avg %</th>
                <th className="px-5 py-2.5 text-right font-medium">Best %</th>
                <th className="px-5 py-2.5 text-right font-medium">Watch time</th>
                <th className="px-5 py-2.5 text-right font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {viewers.map((v) => {
                const active = selected !== null && selected.actor === v.actor;
                return (
                  <tr
                    key={v.actor ?? ''}
                    onClick={() => onSelect(active ? null : { actor: v.actor })}
                    className={`cursor-pointer border-b border-border/40 last:border-0 hover:bg-muted/40 ${active ? 'bg-primary/5' : ''}`}
                  >
                    <td className="max-w-[20rem] truncate px-5 py-2.5">
                      {v.actor ? (
                        <span className="font-mono text-xs">{v.actor}</span>
                      ) : (
                        <span className="text-muted-foreground">Unknown viewers · {v.devices} device{v.devices === 1 ? '' : 's'}</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{v.views}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{v.completes}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{v.avgPercentViewed}%</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{v.bestPercentViewed}%</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{dur(v.watchMs)}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right text-muted-foreground">{new Date(v.lastSeenAt).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {viewers.every((v) => !v.actor) && (
            <p className="border-t border-border/40 px-5 py-2.5 text-xs text-muted-foreground">
              To see each person here, give them their own link: in Publish, open Track who watches on this link and copy a link per viewer id.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden rounded-xl border border-border/60 bg-card"
    >
      <div className="border-b border-border/60 px-5 py-3.5">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="p-5">{children}</div>
    </motion.section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">{children}</div>;
}

// Standalone interactive topic tests attached to this video (linkedVideoToken).
// Each keeps its own share token / event stream — this groups them under the
// video; click through to that assessment's own comprehension dashboard.
function LinkedAssessmentsPanel({
  items,
}: {
  items: NonNullable<Agg['linkedAssessments']>;
}) {
  return (
    <Panel title="Topic tests" subtitle="Interactive assessments attached to this video — each tracked separately">
      <div className="space-y-2">
        {items.map((a) => (
          <Link
            key={a.shareToken}
            to="/analytics/$shareToken"
            params={{ shareToken: a.shareToken }}
            className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-4 py-3 transition hover:bg-muted/40"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{a.title ?? '(untitled assessment)'}</div>
              <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{a.shareToken} · {a.questionCount} questions</div>
            </div>
            <div className="flex shrink-0 items-center gap-5 text-right">
              <div>
                <div className="text-sm font-semibold tabular-nums">{a.attempts}</div>
                <div className="text-[11px] text-muted-foreground">attempts</div>
              </div>
              <div>
                <div className="text-sm font-semibold tabular-nums">{a.answers}</div>
                <div className="text-[11px] text-muted-foreground">answers</div>
              </div>
              <span className="text-xs text-primary">Open →</span>
            </div>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function Bars({ rows, total }: { rows: Array<{ key: string; views: number }>; total: number }) {
  if (!rows || rows.length === 0) return <Empty>No data.</Empty>;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={r.key} className="flex items-center gap-3 text-sm">
          <div className="w-24 shrink-0 truncate text-muted-foreground">{r.key}</div>
          <div className="flex-1">
            <AnimatedBar pct={pct(r.views, total)} height="h-2" delay={i * 0.05} />
          </div>
          <div className="w-10 shrink-0 text-right tabular-nums">{r.views}</div>
        </div>
      ))}
    </div>
  );
}

interface Comprehension {
  totals: { interactions: number; viewers: number; graded: number; correct: number };
  byType: Array<{ type: string; verb: string; n: number }>;
  interactions: Array<{
    id: string;
    type: string;
    total: number;
    graded: number;
    correct: number;
    avgLatencyMs: number | null;
    viewers: number;
    responses: Array<{ response: string; n: number; correct: number }>;
    timing?: QuestionTiming | null;
  }>;
  concepts: Array<{ concept: string; total: number; graded: number; correct: number }>;
  branches: Array<{ branch: string; n: number }>;
  timing?: {
    assessment: { completions: number; avgActiveMs: number | null; avgWallMs: number | null; avgIdleMs: number | null; medianActiveMs: number | null } | null;
    perQuestion: QuestionTiming[];
  };
}
interface QuestionTiming {
  id: string;
  n?: number;
  avgActiveMs: number | null;
  avgVisits: number | null;
  avgPostAnswerMs?: number | null;
  avgTimeToFirstInteractionMs?: number | null;
  avgTimeToAnswerMs: number | null;
  avgTimeToAnswerCorrectMs?: number | null;
  avgTimeToAnswerIncorrectMs?: number | null;
}

// Assessment-only view: the topic-test questions (multiple-choice, match,
// multi-select, ordering, numeric) captured as `assessment_answered` events,
// surfaced as solve rates + NCERT concept mastery. Shares the comprehension
// endpoint; filters to assessment interaction types.
const ASSESSMENT_TYPES = new Set(['multiple-choice', 'multi-select', 'match', 'ordering', 'numeric', 'true-false', 'fill-in', 'hotspot']);

interface AttemptRow {
  viewId: string;
  anonShort: string | null;
  identifiedId: string | null;
  startedAt: string;
  answered: number;
  correct: number;
  total: number | null;
  percent: number | null;
  durationMs: number | null;
  activeMs: number | null;
  idleMs: number | null;
  retried: number | null;
  completed: boolean;
  learnerFirstName: string | null;
  learnerRoll: string | null;
  learnerClass: string | null;
}

// Per-attempt list: one row per (anonymous) taker. Clicking a row opens the
// existing TimelineDrawer for that viewId — their full answer-by-answer timeline.
function AttemptsPanel({ shareToken, onSelect }: { shareToken: string; onSelect: (viewId: string) => void }) {
  const [rows, setRows] = useState<AttemptRow[] | null>(null);
  useEffect(() => {
    api
      .get(`/analytics/dashboard/videos/${shareToken}/assessment/attempts`)
      .then((r) => setRows(r.data.attempts ?? []))
      .catch(() => setRows([]));
  }, [shareToken]);

  if (!rows) return <Panel title="Attempts"><Empty>Loading…</Empty></Panel>;
  if (rows.length === 0)
    return (
      <Panel title="Attempts" subtitle="Every individual taker — appears as each learner takes the test">
        <Empty>No attempts captured yet.</Empty>
      </Panel>
    );

  return (
    <Panel title="Attempts" subtitle="Every individual taker — click a row for their full answer-by-answer timeline">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Taker</th>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-right">Answered</th>
              <th className="px-3 py-2 text-right">Score</th>
              <th className="px-3 py-2 text-right">Engaged</th>
              <th className="px-3 py-2 text-right">Time</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const total = a.total ?? a.answered;
              const scorePct = a.percent ?? (total > 0 ? pct(a.correct, total) : 0);
              return (
                <tr
                  key={a.viewId}
                  className="cursor-pointer border-t transition-colors hover:bg-muted/40"
                  onClick={() => onSelect(a.viewId)}
                >
                  <td className="px-3 py-2 text-xs">
                    {a.learnerFirstName ? (
                      <span className="font-medium">
                        {a.learnerFirstName}
                        {a.learnerRoll ? ` · Roll ${a.learnerRoll}` : ''}
                        {a.learnerClass ? <span className="text-muted-foreground"> · {a.learnerClass}</span> : null}
                      </span>
                    ) : (
                      <span className="font-mono">{a.identifiedId ?? `${a.anonShort ?? '????'}…`}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(a.startedAt).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{a.answered}{total ? ` / ${total}` : ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {a.correct}{total ? `/${total}` : ''} <span className="text-muted-foreground">({scorePct}%)</span>
                    {a.retried ? <span className="ml-1 text-[10px] text-amber-600" title={`${a.retried} question(s) redone`}>↻{a.retried}</span> : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" title={a.idleMs != null ? `${tms(a.idleMs)} idle` : undefined}>{tms(a.activeMs)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{a.durationMs ? tms(a.durationMs) : '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {a.completed ? (
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-600">done</span>
                    ) : (
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600">partial</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground">›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function AssessmentsPanel({ shareToken, onSelect }: { shareToken: string; onSelect: (viewId: string) => void }) {
  const [data, setData] = useState<Comprehension | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api
      .get(`/analytics/dashboard/videos/${shareToken}/comprehension`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load'));
  }, [shareToken]);

  if (err) return <Panel title="Assessment results"><Empty>{err}</Empty></Panel>;
  if (!data) return <Panel title="Assessment results"><Empty>Loading…</Empty></Panel>;

  const questions = (data.interactions ?? []).filter((it) => ASSESSMENT_TYPES.has(it.type));
  if (questions.length === 0) {
    return (
      <div className="space-y-5">
        <Panel title="Assessment results">
          <Empty>
            No assessment answers captured yet. Once learners take the topic test after this video, each question and its
            NCERT concept mastery appears here.
          </Empty>
        </Panel>
        <AttemptsPanel shareToken={shareToken} onSelect={onSelect} />
      </div>
    );
  }
  const graded = questions.reduce((s, q) => s + q.graded, 0);
  const correct = questions.reduce((s, q) => s + q.correct, 0);
  const learners = questions.reduce((m, q) => Math.max(m, q.viewers), 0);

  return (
    <div className="space-y-5">
      <Stagger className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Questions" value={questions.length} accent />
        <StatCard label="Learners" value={learners} />
        <StatCard label="Answers graded" value={graded} />
        <StatCard label="Avg correct" value={graded > 0 ? `${pct(correct, graded)}%` : '—'} accent bar={graded > 0 ? pct(correct, graded) : 0} />
      </Stagger>

      {data.timing?.assessment && data.timing.assessment.completions > 0 && (
        <Panel title="Time on task" subtitle="Engaged time pauses when the tab is hidden; elapsed is wall-clock. Averaged across finished attempts.">
          <Stagger className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Avg engaged" value={tms(data.timing.assessment.avgActiveMs)} accent />
            <StatCard label="Median engaged" value={tms(data.timing.assessment.medianActiveMs)} />
            <StatCard label="Avg elapsed" value={tms(data.timing.assessment.avgWallMs)} />
            <StatCard label="Avg idle / away" value={tms(data.timing.assessment.avgIdleMs)} />
          </Stagger>
        </Panel>
      )}

      {data.concepts.length > 0 && (
        <Panel title="Mastery by NCERT concept" subtitle="Per-concept correctness across all learners — the comprehension layer">
          <div className="space-y-2">
            {[...data.concepts]
              .sort((a, b) => (a.graded ? a.correct / a.graded : 0) - (b.graded ? b.correct / b.graded : 0))
              .map((c) => {
                const p = c.graded > 0 ? pct(c.correct, c.graded) : 0;
                const col = p >= 80 ? 'bg-emerald-500' : p >= 50 ? 'bg-amber-500' : 'bg-rose-500';
                return (
                  <div key={c.concept} className="flex items-center gap-3 text-sm">
                    <span className="w-60 shrink-0 truncate font-mono text-xs">{c.concept}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div className={`h-full rounded-full ${col}`} style={{ width: `${p}%` }} />
                    </div>
                    <span className="w-12 shrink-0 text-right text-xs tabular-nums">{c.graded > 0 ? `${p}%` : '—'}</span>
                  </div>
                );
              })}
          </div>
        </Panel>
      )}

      <Panel title="By question" subtitle="Solve rate, time-to-answer (✓ vs ✗ on first try), reading time, and revisits">
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Question</th>
                <th className="px-3 py-2 text-left">Format</th>
                <th className="px-3 py-2 text-right">Answers</th>
                <th className="px-3 py-2 text-right">Correct</th>
                <th className="px-3 py-2 text-right">Avg answer</th>
                <th className="px-3 py-2 text-right" title="Avg time-to-answer on a correct first try">✓ time</th>
                <th className="px-3 py-2 text-right" title="Avg time-to-answer on an incorrect first try">✗ time</th>
                <th className="px-3 py-2 text-right" title="Avg time before the first interaction (reading/thinking)">Read</th>
                <th className="px-3 py-2 text-right" title="Avg visits to this question">Visits</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((it) => {
                const t = it.timing;
                return (
                  <tr key={it.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{it.id}</td>
                    <td className="px-3 py-2">{it.type}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{it.total}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{it.graded > 0 ? `${pct(it.correct, it.graded)}%` : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{tms(t?.avgTimeToAnswerMs ?? it.avgLatencyMs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{tms(t?.avgTimeToAnswerCorrectMs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-600">{tms(t?.avgTimeToAnswerIncorrectMs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{tms(t?.avgTimeToFirstInteractionMs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{t?.avgVisits != null ? t.avgVisits.toFixed(1) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <AttemptsPanel shareToken={shareToken} onSelect={onSelect} />
    </div>
  );
}

// Format-agnostic interaction/comprehension view: graded interactions show
// correctness, ungraded (drags/explores) show counts, branches show path
// distribution, tagged content shows concept mastery. Sections hide when empty.
function ComprehensionPanel({ shareToken }: { shareToken: string }) {
  const [data, setData] = useState<Comprehension | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .get(`/analytics/dashboard/videos/${shareToken}/comprehension`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load'));
  }, [shareToken]);

  const title = 'Comprehension & interactions';
  if (err) return <Panel title={title}><Empty>{err}</Empty></Panel>;
  if (!data) return <Panel title={title}><Empty>Loading…</Empty></Panel>;
  if (!data.totals || data.totals.interactions === 0) {
    return (
      <Panel title={title}>
        <Empty>
          No interactions captured yet. Quizzes, drags, sliders, and branches appear here as people engage with
          interactive slides.
        </Empty>
      </Panel>
    );
  }

  const t = data.totals;
  return (
    <Panel title={title}>
      <div className="space-y-6">
        <Stagger className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Interactions" value={t.interactions} />
          <StatCard label="People" value={t.viewers} />
          {t.graded > 0 && <StatCard label="Correct" value={`${pct(t.correct, t.graded)}%`} />}
          {t.graded > 0 && <StatCard label="Graded" value={t.graded} />}
        </Stagger>

        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">By interaction</div>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Element</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Times</th>
                  <th className="px-3 py-2 text-right">People</th>
                  <th className="px-3 py-2 text-right">Correct</th>
                  <th className="px-3 py-2 text-right">Avg time</th>
                </tr>
              </thead>
              <tbody>
                {data.interactions.map((it) => (
                  <Fragment key={it.id}>
                    <tr className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{it.id}</td>
                      <td className="px-3 py-2">{it.type}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{it.total}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{it.viewers}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{it.graded > 0 ? `${pct(it.correct, it.graded)}%` : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{it.avgLatencyMs != null ? `${(it.avgLatencyMs / 1000).toFixed(1)}s` : '—'}</td>
                    </tr>
                    {it.responses.length > 0 && (
                      <tr className="border-t bg-muted/20">
                        <td colSpan={6} className="px-3 py-2">
                          <div className="space-y-1">
                            {it.responses.map((r) => (
                              <div key={r.response} className="flex items-center gap-2 text-xs">
                                <span className="w-28 shrink-0 truncate">{r.response}</span>
                                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                                  <div
                                    className={`h-full rounded-full ${r.correct > 0 ? 'bg-primary' : 'bg-muted-foreground/40'}`}
                                    style={{ width: `${pct(r.n, it.total)}%` }}
                                  />
                                </div>
                                <span className="w-16 shrink-0 text-right tabular-nums">
                                  {r.n}{r.correct > 0 ? ' ✓' : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {data.concepts.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-medium text-muted-foreground">Concept mastery</div>
            <div className="space-y-2">
              {data.concepts.map((c) => (
                <div key={c.concept} className="flex items-center gap-3 text-sm">
                  <span className="w-32 shrink-0 truncate">{c.concept}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${c.graded > 0 ? pct(c.correct, c.graded) : 0}%` }} />
                  </div>
                  <span className="w-16 shrink-0 text-right text-xs tabular-nums">
                    {c.graded > 0 ? `${pct(c.correct, c.graded)}%` : `${c.total}×`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.branches.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-medium text-muted-foreground">Branch paths</div>
            <Bars
              rows={data.branches.map((b) => ({ key: b.branch, views: b.n }))}
              total={data.branches.reduce((a, b) => a + b.n, 0)}
            />
          </div>
        )}
      </div>
    </Panel>
  );
}

function RetentionCurve({ data, totalViews }: { data: Array<{ second: number; reached: number; plays: number }>; totalViews: number }) {
  if (!data || data.length === 0) {
    return (
      <Panel title="Audience retention">
        <Empty>No second-by-second coverage yet (needs at least one completed view).</Empty>
      </Panel>
    );
  }
  const W = 1000;
  const H = 200;
  const { points: line, area, maxSec } = smoothRetention(data, totalViews);
  return (
    <Panel title="Audience retention" subtitle="Share of viewers still watching at each point in the video">
      <div className="flex gap-3">
        <div className="flex h-52 w-9 shrink-0 flex-col justify-between py-1 text-right text-[10px] tabular-nums text-muted-foreground">
          <span>100%</span>
          <span>75%</span>
          <span>50%</span>
          <span>25%</span>
          <span>0%</span>
        </div>
        <div className="min-w-0 flex-1">
          <svg viewBox={`0 0 ${W} ${H}`} className="h-52 w-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="retentionArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.22" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[0, 0.25, 0.5, 0.75, 1].map((g) => (
              <line
                key={g}
                x1={0}
                x2={W}
                y1={g * H}
                y2={g * H}
                className="stroke-border/70"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                strokeDasharray={g === 1 ? undefined : '3 4'}
              />
            ))}
            <motion.path d={area} fill="url(#retentionArea)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }} />
            <motion.path
              d={line}
              className="fill-none stroke-primary"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            />
          </svg>
          <div className="mt-2 flex justify-between text-[11px] tabular-nums text-muted-foreground">
            <span>0:00</span>
            <span>{clock(maxSec * 1000)}</span>
          </div>
        </div>
      </div>
    </Panel>
  );
}

interface ViewRecord {
  viewId: string;
  sessionId: string;
  anonymousId: string;
  identifiedId: string | null;
  startedAt: string;
  endedAt: string | null;
  maxPercent: number;
  completed: boolean;
  deviceType: string | null;
  geoCountry: string | null;
  embedOrigin: string | null;
  referrer: string | null;
  variant: string;
  coverageVector: number[];
}

interface ViewDetail {
  view: ViewRecord;
  context: Record<string, any>;
  events: TimelineEvent[];
  interactions: Array<Record<string, unknown>>;
}

function TimelineDrawer({ viewId, admin, onClose }: { viewId: string; admin: boolean; onClose: () => void }) {
  const [data, setData] = useState<ViewDetail | null>(null);

  useEffect(() => {
    api.get(`/analytics/dashboard/views/${viewId}`).then((r) => setData(r.data));
  }, [viewId]);

  const startMs = data?.events?.[0] ? new Date(data.events[0].viewerTime).getTime() : 0;
  const ctx = data?.context ?? {};
  const ua = ctx.uaData ?? {};
  const view = data?.view;

  const groups = useMemo(() => groupBySlide(data?.events ?? []), [data]);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const allOpen = groups.length > 0 && open.size === groups.length;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="h-full w-full max-w-2xl overflow-y-auto border-l border-border/60 bg-background p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 34 }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-medium">Everything we captured for this view</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        {!data || !view ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            {/* Identity + environment */}
            <div className="mb-5 grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border p-4 text-sm">
              {(view as { identifiedId?: string | null }).identifiedId ? (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Learner</div>
                  {admin ? (
                    <Link
                      to="/analytics/actors/$actorId"
                      params={{ actorId: String((view as { identifiedId?: string | null }).identifiedId) }}
                      className="break-all font-mono text-sm text-primary hover:underline"
                    >
                      {String((view as { identifiedId?: string | null }).identifiedId)}
                    </Link>
                  ) : (
                    <div className="break-all font-mono text-sm">{String((view as { identifiedId?: string | null }).identifiedId)}</div>
                  )}
                </div>
              ) : null}
              <Field label="Viewer (anon)" value={`${view.anonymousId.slice(0, 16)}…`} mono />
              <Field label="Session" value={`${view.sessionId.slice(0, 8)}…`} mono />
              <Field label="Device" value={view.deviceType ?? '—'} />
              <Field label="Browser / OS" value={`${ua.brands?.[0]?.brand ?? '?'} ${ua.brands?.[0]?.version ?? ''} · ${ua.platform ?? '?'}`} />
              <Field label="Country" value={view.geoCountry ?? '—'} />
              <Field label="Network" value={ctx.network ? `${ctx.network.effectiveType ?? '?'} · ${ctx.network.downlink ?? '?'}Mbps` : '—'} />
              <Field label="Locale" value={ctx.locale ?? '—'} />
              <Field label="Timezone" value={ctx.timezone ?? '—'} />
              <Field label="Screen" value={ctx.screen ? `${ctx.screen.w}×${ctx.screen.h}@${ctx.screen.dpr}` : '—'} />
              <Field label="Viewport" value={ctx.viewport ? `${ctx.viewport.w}×${ctx.viewport.h}` : '—'} />
              <Field label="Referrer" value={view.referrer ?? ctx.page?.referrer ?? '(direct)'} />
              <Field label="Embed origin" value={view.embedOrigin ?? '(first-party)'} />
              <Field label="UTM source" value={ctx.campaign?.source ?? '—'} />
              <Field label="Variant" value={view.variant} />
              <Field label="Max % viewed" value={`${view.maxPercent}%`} />
              <Field label="Completed" value={view.completed ? 'yes' : 'no'} />
              <Field label="Started" value={new Date(view.startedAt).toLocaleString()} />
              <Field label="Ended" value={view.endedAt ? new Date(view.endedAt).toLocaleString() : '—'} />
            </div>

            {/* Per-second coverage */}
            {view.coverageVector?.length > 0 && (
              <div className="mb-6">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Watch coverage</span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {view.coverageVector.filter((v) => v > 0).length}/{view.coverageVector.length}s watched
                  </span>
                </div>
                <CoverageHeatmap vector={view.coverageVector} />
                <div className="mt-1.5 flex items-center gap-4 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-muted ring-1 ring-inset ring-border/60" /> skipped</span>
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: 'hsl(var(--primary) / 0.6)' }} /> watched</span>
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: 'hsl(var(--primary))' }} /> rewatched</span>
                </div>
              </div>
            )}

            {/* Event stream, collapsed by slide */}
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-medium text-muted-foreground">
                Timeline · {data.events.length} events across {groups.length} slide{groups.length === 1 ? '' : 's'}
              </div>
              <button
                onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map((_, i) => i)))}
                className="text-xs text-primary hover:underline"
              >
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            <div className="space-y-2">
              {groups.map((g, gi) => {
                const isOpen = open.has(gi);
                const interactions = g.events.filter((e) => e.event.startsWith('gate_') || e.event === 'branch_selected').length;
                return (
                  <div key={gi} className="overflow-hidden rounded-lg border border-border/60">
                    <button
                      onClick={() => toggle(gi)}
                      className="flex w-full items-center gap-3 bg-muted/30 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <span className={`text-xs text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                      <span className="flex h-6 min-w-[1.5rem] items-center justify-center rounded bg-primary/10 px-1.5 text-xs font-semibold text-primary">
                        {g.slideIndex != null && g.slideIndex >= 0 ? g.slideIndex + 1 : '•'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{g.slideId || 'Session'}</div>
                        <div className="text-[11px] text-muted-foreground">
                          +{((g.startTs - startMs) / 1000).toFixed(0)}s · {g.events.length} events
                          {interactions > 0 && <span className="text-primary"> · {interactions} interaction{interactions === 1 ? '' : 's'}</span>}
                        </div>
                      </div>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <ol className="divide-y divide-border/40 px-1 py-1">
                            {g.events.map((e, i) => (
                              <li key={i} className="px-2 py-1.5 text-sm">
                                <div className="flex items-start gap-3">
                                  <span className="w-10 shrink-0 text-right font-mono text-xs text-muted-foreground">
                                    +{((new Date(e.viewerTime).getTime() - startMs) / 1000).toFixed(0)}s
                                  </span>
                                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${eventColor(e.event)}`} />
                                  <div className="min-w-0 flex-1">
                                    <span className="font-medium">{eventLabel(e.event)}</span>
                                    {e.playheadMs != null && <span className="ml-2 text-xs text-muted-foreground">@{clock(e.playheadMs)}</span>}
                                    {actorBadge(e.properties)}
                                    <PropChips properties={e.properties} />
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ol>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

function CoverageHeatmap({ vector }: { vector: number[] }) {
  // One <div> per second collapses to 0-width for long videos. Bucket into a
  // fixed number of columns and colour each by how much of it was watched.
  const COLS = 180;
  const n = vector.length;
  const size = Math.max(1, Math.ceil(n / Math.min(COLS, n)));
  const buckets: Array<{ start: number; end: number; watched: number; rewatch: boolean }> = [];
  for (let i = 0; i < n; i += size) {
    const slice = vector.slice(i, i + size);
    const seen = slice.filter((v) => v > 0).length;
    buckets.push({
      start: i,
      end: Math.min(n, i + size),
      watched: seen / slice.length,
      rewatch: slice.some((v) => v > 1),
    });
  }
  return (
    <div className="flex h-12 w-full overflow-hidden rounded-md bg-muted ring-1 ring-inset ring-border/60">
      {buckets.map((b, i) => {
        const op = b.watched === 0 ? 0 : 0.22 + b.watched * 0.78;
        return (
          <div
            key={i}
            title={`${clock(b.start * 1000)}–${clock(b.end * 1000)}: ${Math.round(b.watched * 100)}% watched${b.rewatch ? ' (rewatched)' : ''}`}
            className="flex-1"
            style={{ backgroundColor: b.rewatch ? 'hsl(var(--primary))' : `hsl(var(--primary) / ${op})` }}
          />
        );
      })}
    </div>
  );
}

function PropChips({ properties }: { properties: Record<string, unknown> }) {
  const entries = Object.entries(properties || {}).filter(
    ([k, v]) => v != null && k !== 'seconds' && !(Array.isArray(v) && v.length > 24),
  );
  if (entries.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span key={k} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
        </span>
      ))}
    </div>
  );
}

interface SlideGroup {
  slideId: string | null;
  slideIndex: number | null;
  events: TimelineEvent[];
  startTs: number;
}

/** Group the event stream into consecutive runs per slide (null-slide events
 *  — view_end, tab_away — fold into the surrounding slide). */
function groupBySlide(events: TimelineEvent[]): SlideGroup[] {
  const groups: SlideGroup[] = [];
  for (const e of events) {
    const ts = new Date(e.viewerTime).getTime();
    const last = groups[groups.length - 1];
    if (last && (e.slideId == null || e.slideId === last.slideId)) {
      last.events.push(e);
    } else {
      groups.push({ slideId: e.slideId ?? null, slideIndex: e.slideIndex ?? null, events: [e], startTs: ts });
    }
  }
  return groups;
}

/** A colored dot keyed to the event family. */
function eventColor(event: string): string {
  if (event.startsWith('gate_') || event === 'branch_selected' || event === 'cta_click') return 'bg-teal-500';
  if (event === 'video_seek') return 'bg-amber-500';
  if (event === 'video_pause' || event === 'video_resume') return 'bg-orange-400';
  if (['video_load', 'video_start', 'video_complete', 'view_end'].includes(event)) return 'bg-primary';
  return 'bg-muted-foreground/40';
}

function eventLabel(event: string): string {
  return event.replace(/^video_/, '').replace(/_/g, ' ');
}

/** Tag seeks/pauses as user vs framework using the captured provenance. */
function actorBadge(p: Record<string, unknown>): JSX.Element | null {
  const u = p?.user;
  const s = p?.source;
  const isFramework = u === false || u === 'false' || s === 'transition' || u === 'auto_advance';
  const isUser = u === true || u === 'true' || s === 'user';
  if (isFramework)
    return <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">framework</span>;
  if (isUser)
    return <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">user</span>;
  return null;
}
